import { createContext, useContext, useEffect, useMemo, useState, type PropsWithChildren } from "react";
import { api } from "./api/client";
import type { User } from "./api/types";
import { startPushNotifications, stopPushNotifications } from "./push";

type AuthState = {
  user: User | null;
  loading: boolean;
  login(email: string, password: string): Promise<void>;
  logout(): Promise<void>;
};

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: PropsWithChildren) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    api.restore().catch(() => null).then((restored) => {
      if (active) {
        setUser(restored);
        setLoading(false);
        if (restored) void startPushNotifications().catch(() => undefined);
      }
    });
    return () => { active = false; };
  }, []);

  const value = useMemo<AuthState>(() => ({
    user,
    loading,
    async login(email, password) {
      const session = await api.login(email, password);
      setUser(session.user);
      await startPushNotifications().catch(() => undefined);
    },
    async logout() {
      await stopPushNotifications();
      await api.logout();
      setUser(null);
    },
  }), [loading, user]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used inside AuthProvider");
  return value;
}
